import { supabase } from '@/lib/supabase';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

export type BusinessMember = {
  business_id: string;
  business_name: string;
  my_role: 'owner' | 'member';
  member_id: string;
  member_name: string | null;
  member_avatar_url: string | null;
  member_role: 'owner' | 'member';
  member_joined_at: string;
  member_equity_percent: number | null;
  /** Αν true, αυτό το μέλος επέλεξε να εμφανίζεται στην αναζήτηση με την
   * ταυτότητα της κοινής επιχείρησης αντί για το δικό του προσωπικό
   * επάγγελμα (default: false — προσωπικό πρώτα). */
  member_shows_as_business: boolean;
};

export type PendingBusinessInvite = {
  invite_id: string;
  business_id: string;
  business_name: string;
  invited_by_name: string | null;
  created_at: string;
};

export type SentBusinessInvite = {
  invite_id: string;
  business_id: string;
  invited_profile_id: string | null;
  invited_name: string | null;
  invited_avatar_url: string | null;
  invited_email: string | null;
  created_at: string;
};

/** Εκκρεμείς προσκλήσεις που έστειλε ο ιδιοκτήτης — για να εμφανίζονται σαν
 * "προσκεκλημένος" στη λίστα μελών, πριν γίνουν αποδεκτές. */
export async function listMySentBusinessInvites(): Promise<SentBusinessInvite[]> {
  const { data, error } = await db.rpc('list_my_sent_business_invites');
  if (error) throw new Error(error.message);
  return (data ?? []) as SentBusinessInvite[];
}

export async function cancelBusinessInvite(inviteId: string): Promise<void> {
  const { error } = await db.rpc('cancel_business_invite', { p_invite_id: inviteId });
  if (error) throw new Error(error.message);
}

export async function createBusiness(name: string): Promise<string> {
  const { data, error } = await db.rpc('create_business', { p_name: name });
  if (error) throw new Error(error.message);
  return data as string;
}

export async function inviteBusinessMember(profileId: string, equityPercent?: number | null): Promise<void> {
  const { error } = await db.rpc('invite_business_member', { p_profile_id: profileId, p_equity_percent: equityPercent ?? null });
  if (error) throw new Error(error.message);
}

/** Ομαδοποιεί τα flat rows του list_my_business() σε μία κάρτα ανά επιχείρηση —
 * ένας χρήστης μπορεί πλέον να είναι μέλος (όχι ιδιοκτήτης) περισσότερων από μία. */
export type MyBusiness = {
  business_id: string;
  business_name: string;
  my_role: 'owner' | 'member';
  members: BusinessMember[];
};

export async function listMyBusinesses(): Promise<MyBusiness[]> {
  const rows = await listMyBusiness();
  const byId = new Map<string, MyBusiness>();
  for (const row of rows) {
    let entry = byId.get(row.business_id);
    if (!entry) {
      entry = { business_id: row.business_id, business_name: row.business_name, my_role: row.my_role, members: [] };
      byId.set(row.business_id, entry);
    }
    entry.members.push(row);
  }
  return Array.from(byId.values());
}

export async function respondBusinessInvite(inviteId: string, accept: boolean): Promise<void> {
  const { error } = await db.rpc('respond_business_invite', { p_invite_id: inviteId, p_accept: accept });
  if (error) throw new Error(error.message);
}

export async function listMyPendingBusinessInvites(): Promise<PendingBusinessInvite[]> {
  const { data, error } = await db.rpc('list_my_pending_business_invites');
  if (error) throw new Error(error.message);
  return (data ?? []) as PendingBusinessInvite[];
}

export async function listMyBusiness(): Promise<BusinessMember[]> {
  const { data, error } = await db.rpc('list_my_business');
  if (error) throw new Error(error.message);
  return (data ?? []) as BusinessMember[];
}

export type BusinessIdentityFields = {
  business_name: string | null;
  business_logo_url: string | null;
  professional_terms: string | null;
  show_business_name: boolean;
  /** Το profile_id του δημιουργού/υπεύθυνου της επιχείρησης — χρήσιμο για να
   * ξέρει ο καλών αν ο ίδιος ΕΙΝΑΙ ο δημιουργός (οπότε "εγώ" και "η επιχείρηση"
   * ταυτίζονται και δεν χρειάζεται καν επιλογή). */
  created_by: string | null;
};

/** Στοιχεία ταυτότητας μιας επιχείρησης — το ΠΡΑΓΜΑΤΙΚΟ όνομα από
 * businesses.name (όχι από το προφίλ κάποιου μέλους!), συν λογότυπο/όρους από
 * τον δημιουργό της, για χρήση σε PDF/"αποστολή ως η επιχείρηση". Περνάει
 * από RPC (SECURITY DEFINER) ώστε να δουλεύει ανεξάρτητα από RLS στο προφίλ
 * του δημιουργού, αρκεί ο καλών να είναι μέλος της επιχείρησης. */
export async function getBusinessIdentityFields(businessId: string): Promise<BusinessIdentityFields | null> {
  const { data, error } = await db.rpc('get_business_identity', { p_business_id: businessId });
  const row = (data as any[] | null)?.[0];
  if (error || !row) return null;
  return {
    business_name: row.business_name ?? null,
    business_logo_url: row.business_logo_url ?? null,
    professional_terms: row.professional_terms ?? null,
    show_business_name: true,
    created_by: row.created_by ?? null,
  };
}

export async function leaveBusiness(businessId: string): Promise<void> {
  const { error } = await db.rpc('leave_business', { p_business_id: businessId });
  if (error) throw new Error(error.message);
}

export async function removeBusinessMember(profileId: string): Promise<void> {
  const { error } = await db.rpc('remove_business_member', { p_profile_id: profileId });
  if (error) throw new Error(error.message);
}

/** Ο χρήστης επιλέγει αν θέλει να εμφανίζεται στην αναζήτηση με την
 * ταυτότητα της κοινής επιχείρησης αντί για το δικό του προσωπικό επάγγελμα
 * (default: false — πάντα προσωπικό, εκτός αν το αλλάξει ο ίδιος). */
export async function setBusinessSearchIdentity(businessId: string, showAsBusiness: boolean): Promise<void> {
  const { error } = await db.rpc('set_business_search_identity', { p_business_id: businessId, p_show_as_business: showAsBusiness });
  if (error) throw new Error(error.message);
}

/** Πρόσκληση συνεταίρου με email — για όποιον δεν είναι ακόμα στη Χειραψία.
 * Δημιουργεί την πρόσκληση και του στέλνει email να εγγραφεί. */
export async function invitePartnerByEmail(email: string, equityPercent?: number | null): Promise<void> {
  const { data, error } = await db.rpc('invite_business_partner_by_email', { p_email: email, p_equity_percent: equityPercent ?? null });
  if (error) throw new Error(error.message);
  const inviteId = (data as { invite_id: string }[] | null)?.[0]?.invite_id;
  if (inviteId) {
    try {
      await supabase.functions.invoke('notify-business-invite', { body: { inviteId } });
    } catch { /* το invite δημιουργήθηκε ούτως ή άλλως — η αποτυχία email δεν μπλοκάρει */ }
  }
}
